import { useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, DownloadSimpleIcon, UploadSimpleIcon } from '@phosphor-icons/react';

import {
  SCOPE_LABELS,
  deleteSkillFile,
  getSkillFile,
  importOverSkillZip,
  putSkillFile,
  skillExportHref,
  updateSkill,
  useSkill,
} from '@/api-models/skills';
import { useSpace } from '@/contexts/space-context';
import { settingsBase } from '@/features/settings/components/nav-config';
import { SkillEditor, type SkillEditorApi } from '@/features/settings/components/SkillEditor';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/button/Button';
import { Spinner } from '@/ui/Spinner';

/**
 * Settings → Skills → one skill.
 *
 * A route rather than a modal: a skill is a document you sit with, and a bundle needs a file tree
 * beside an editor — three nested scroll contexts in a dialog, with no URL to reload.
 */
export function SkillDetailPage() {
  const { skillId } = useParams<{ skillId: string }>();
  const { teamId } = useSpace();
  const { skill, mutate, isLoading } = useSkill(teamId, skillId ?? null);
  const fileInput = useRef<HTMLInputElement>(null);

  const backHref = `${settingsBase(teamId)}/automation/skills`;
  // System skills are read-only here; the list offers Fork as the way to change one.
  const readOnly = skill?.scope === 'system';
  const ownerTeamId = skill?.team_id ?? null;

  const api: SkillEditorApi = useMemo(
    () => ({
      loadFile: async (path) => {
        const file = await getSkillFile(teamId, skill!.id, path);
        // A binary file never reaches here — the editor shows a placeholder instead.
        return file.content ?? '';
      },
      saveSkillMd: async (source) => {
        if (!ownerTeamId) throw new Error('This skill is read-only.');
        await updateSkill(ownerTeamId, skill!.id, { skill_md: source, enabled: skill!.enabled });
      },
      saveFile: async (path, content) => {
        if (!ownerTeamId) throw new Error('This skill is read-only.');
        await putSkillFile(ownerTeamId, skill!.id, path, content);
      },
      createFile: async (path) => {
        if (!ownerTeamId) throw new Error('This skill is read-only.');
        await putSkillFile(ownerTeamId, skill!.id, path, '');
      },
      deleteFile: async (path) => {
        if (!ownerTeamId) throw new Error('This skill is read-only.');
        await deleteSkillFile(ownerTeamId, skill!.id, path);
      },
      refresh: async () => {
        await mutate();
      },
    }),
    [teamId, ownerTeamId, skill, mutate],
  );

  if (isLoading && !skill) {
    return (
      <div className="flex items-center gap-2 text-sm text-fg-tertiary">
        <Spinner size={4} /> Loading skill…
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink href={backHref} />
        <p className="text-sm text-fg-tertiary">This skill no longer exists.</p>
      </div>
    );
  }

  async function handleReplace(file: File) {
    if (!ownerTeamId || !skill) return;
    try {
      await importOverSkillZip(ownerTeamId, skill.id, file);
      await mutate();
      toast.success('Replaced from the archive');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not import that zip');
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <BackLink href={backHref} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2.5">
            <h1 className="page-title">{skill.name}</h1>
            <Badge variant="neutral" size="md">
              {SCOPE_LABELS[skill.scope]}
            </Badge>
            {!skill.enabled && (
              <Badge variant="neutral" size="md">
                Off
              </Badge>
            )}
          </div>
          <p className="page-subtitle">{skill.description}</p>
        </div>

        <div className="flex items-center gap-2">
          <a href={skillExportHref(teamId, skill.id)} download>
            <Button variant="secondary" size={9} iconLeft={<DownloadSimpleIcon size={16} />}>
              Export
            </Button>
          </a>
          {!readOnly && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept=".zip,application/zip"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void handleReplace(file);
                }}
              />
              <Button
                variant="secondary"
                size={9}
                onClick={() => fileInput.current?.click()}
                iconLeft={<UploadSimpleIcon size={16} />}
              >
                Replace from zip
              </Button>
            </>
          )}
        </div>
      </div>

      {readOnly && (
        <p className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-xs text-fg-tertiary">
          System skills are maintained by Zooid staff, so this one can&rsquo;t be edited here. Fork it from the list to
          get an editable copy in this space, or turn it off if it doesn&rsquo;t suit you.
        </p>
      )}

      <SkillEditor skill={skill} readOnly={readOnly} api={api} />
    </div>
  );
}

function BackLink({ href }: { href: string }) {
  return (
    <Link to={href} className="inline-flex w-fit items-center gap-1.5 text-xs text-fg-tertiary hover:text-fg">
      <ArrowLeftIcon size={14} />
      All skills
    </Link>
  );
}
